import { useEffect, useState } from "react";

import { rememberedValue, rememberValue } from "./hooks";
import { supabase } from "./supabase";
import {
  othersOn,
  workingOnTopic,
  type Worker,
  type WorkingOnPayload,
} from "./workingOn";

/**
 * How long after leaving a customer's screens coming back is still the same
 * stay. Moving from the customer file to job #1076, or from #1076 to #1077,
 * unmounts one screen and mounts the next within milliseconds; a colleague
 * must not be told about that as a new arrival (ADR 0268, issue #756). A rep
 * who is away longer than this has left, and is announced again on return.
 */
export const STAY_GRACE_MS = 60_000;

const stayKey = (contactId: string) => `working-on-since:${contactId}`;

/**
 * When this tab's stay on the customer began: the one already running, if the
 * tab is still on the customer or left it within `STAY_GRACE_MS`, else now.
 * Kept in the tab's sessionStorage, so a refresh is the same stay too.
 */
export function beginStay(contactId: string, now = Date.now()): string {
  let since = new Date(now).toISOString();
  const raw = rememberedValue(stayKey(contactId));
  if (raw) {
    try {
      const kept = JSON.parse(raw) as { since?: unknown; leftAt?: unknown };
      const stillHere =
        typeof kept.leftAt !== "number" || now - kept.leftAt <= STAY_GRACE_MS;
      if (typeof kept.since === "string" && stillHere) since = kept.since;
    } catch {
      /* Unreadable: a new stay, which only means one extra pop-up. */
    }
  }
  rememberValue(stayKey(contactId), JSON.stringify({ since }));
  return since;
}

/** This tab left the customer's screen; the stay ends unless it is back soon. */
export function leaveStay(
  contactId: string,
  since: string,
  now = Date.now(),
): void {
  rememberValue(stayKey(contactId), JSON.stringify({ since, leftAt: now }));
}

/**
 * Announce that I have this customer open, and return who else does.
 *
 * `me` is who to announce as; `where` is which job, or null from the
 * customer file. Resubscribes when any of it changes, which is cheap and
 * keeps the lifecycle obvious: one channel per mounted screen, removed with
 * it. The stay's `since` outlives the channel (`beginStay`).
 *
 * The channel is private: Realtime admits a client only when a policy on
 * realtime.messages allows it, and those allow staff alone (migration
 * 20261006100000, issue #755). A public channel could be joined by anyone
 * holding the publishable key, who could read which rep had which job open
 * and announce a rep who was not there.
 */
export function useWorkingOn(
  contactId: string | null | undefined,
  me: { profileId: string | null; name: string },
  where: { jobId: string; jobNumber: number | null } | null,
): Worker[] {
  const [others, setOthers] = useState<Worker[]>([]);
  const myId = me.profileId;
  const jobId = where?.jobId ?? null;
  const jobNumber = where?.jobNumber ?? null;

  useEffect(() => {
    if (!contactId || !myId) {
      setOthers([]);
      return;
    }
    const since = beginStay(contactId);
    const payload: WorkingOnPayload = {
      profileId: myId,
      name: me.name,
      jobId,
      jobNumber,
      since,
    };
    const channel = supabase.channel(workingOnTopic(contactId), {
      config: { private: true },
    });
    channel
      .on("presence", { event: "sync" }, () => {
        setOthers(othersOn(channel.presenceState<WorkingOnPayload>(), myId));
      })
      .subscribe((status) => {
        if (status === "SUBSCRIBED") void channel.track(payload);
      });
    return () => {
      leaveStay(contactId, since);
      setOthers([]);
      void supabase.removeChannel(channel);
    };
  }, [contactId, myId, me.name, jobId, jobNumber]);

  return others;
}
